/**
 * Page d'administration : liste des utilisateurs.
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getUsers, updateUser, deleteUser, resetPassword } from "../api/adminApi";
import SortableTable from "../components/SortableTable";
import ConfirmDialog from "../components/ConfirmDialog";
import StatusBadge from "../components/StatusBadge";
import AdminNav from "../components/AdminNav";
import "./PageStyles.css";

const ROLE_LABELS = {
  admin: "Admin",
  user: "User",
};

const ROLE_VARIANTS = {
  admin: "info",
  user: "secondary",
};

export default function AdminUserListPage() {
  const [users, setUsers] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [resetId, setResetId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [success, setSuccess] = useState("");

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    const data = await getUsers();
    setUsers(data);
  }

  async function handleDelete() {
    await deleteUser(deleteId);
    setDeleteId(null);
    loadUsers();
  }

  async function handleResetPassword() {
    if (!newPassword.trim()) return;
    await resetPassword(resetId, newPassword);
    setResetId(null);
    setNewPassword("");
    setSuccess("Mot de passe réinitialisé avec succès.");
    setTimeout(() => setSuccess(""), 3000);
  }

  function openEdit(u) {
    setEditUser({
      id: u.id,
      first_name: u.first_name,
      last_name: u.last_name,
      email: u.email,
      role: u.role,
    });
  }

  async function handleSaveUser() {
    if (!editUser.first_name.trim() || !editUser.last_name.trim() || !editUser.email.trim()) return;
    await updateUser(editUser.id, {
      first_name: editUser.first_name,
      last_name: editUser.last_name,
      email: editUser.email,
      role: editUser.role,
    });
    setEditUser(null);
    setSuccess("Utilisateur mis à jour.");
    setTimeout(() => setSuccess(""), 3000);
    loadUsers();
  }

  const columns = [
    { key: "last_name", label: "Nom" },
    { key: "first_name", label: "Prénom" },
    { key: "email", label: "Email" },
    { key: "role", label: "Rôle" },
    { key: "operations", label: "" },
  ];

  function renderRow(u, _i, hiddenCols) {
    const h = hiddenCols || {};
    return (
      <tr key={u.id}>
        {!h.last_name && <td>{u.last_name}</td>}
        {!h.first_name && <td>{u.first_name}</td>}
        {!h.email && <td>{u.email}</td>}
        {!h.role && (
          <td>
            <StatusBadge
              value={u.role}
              label={ROLE_LABELS[u.role] || u.role}
              variant={ROLE_VARIANTS[u.role] || "secondary"}
            />
          </td>
        )}
        {!h.operations && (
          <td className="row-actions">
            <div className="row-actions-inner">
              <button className="btn btn-small" onClick={() => openEdit(u)}>
                Modifier
              </button>
              <button className="btn btn-small" onClick={() => setResetId(u.id)}>
                Réinit. MDP
              </button>
              <button className="btn btn-small btn-danger" onClick={() => setDeleteId(u.id)}>
                Supprimer
              </button>
            </div>
          </td>
        )}
      </tr>
    );
  }

  return (
    <div className="page">
      <AdminNav />
      <div className="page-header">
        <h2>Utilisateurs</h2>
        <Link to="/admin/users/new" className="btn btn-primary">
          Nouvel utilisateur
        </Link>
      </div>
      {success && <div className="success-message">{success}</div>}
      <SortableTable
        columns={columns}
        rows={users}
        renderRow={renderRow}
        emptyMessage="Aucun utilisateur."
      />
      {deleteId && (
        <ConfirmDialog
          message="Supprimer cet utilisateur ? Les contacts privés seront supprimés."
          onConfirm={handleDelete}
          onCancel={() => setDeleteId(null)}
        />
      )}
      {resetId && (
        <div className="confirm-overlay">
          <div className="confirm-box">
            <p>Nouveau mot de passe provisoire :</p>
            <div className="form-group">
              <input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nouveau mot de passe"
                autoFocus
              />
            </div>
            <div className="confirm-actions">
              <button
                className="btn btn-secondary"
                onClick={() => { setResetId(null); setNewPassword(""); }}
              >
                Annuler
              </button>
              <button className="btn btn-primary" onClick={handleResetPassword}>
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      )}
      {editUser && (
        <div className="confirm-overlay">
          <div className="confirm-box" style={{ maxWidth: "450px" }}>
            <h3 style={{ marginTop: 0 }}>Modifier l'utilisateur</h3>
            <div className="form-group">
              <label>Prénom</label>
              <input
                type="text"
                value={editUser.first_name}
                onChange={(e) => setEditUser({ ...editUser, first_name: e.target.value })}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Nom</label>
              <input
                type="text"
                value={editUser.last_name}
                onChange={(e) => setEditUser({ ...editUser, last_name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={editUser.email}
                onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Rôle</label>
              <select
                value={editUser.role}
                onChange={(e) => setEditUser({ ...editUser, role: e.target.value })}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="confirm-actions">
              <button className="btn btn-secondary" onClick={() => setEditUser(null)}>
                Annuler
              </button>
              <button className="btn btn-primary" onClick={handleSaveUser}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
